import {
  Box,
  Button as ChakraButton,
  Flex,
  FormControl,
  FormLabel,
  Input,
  useToast,
} from '@chakra-ui/react';
import type { SelectOption } from '@diamond/shared/types';
import { Modal, Select, showToast } from '@diamond/shared/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCreateLHCMutation } from '../hooks';
import type { CreateLHCModalProps } from '../types';
import { SyncInfo } from './sync-info';

export function CreateLHCModal({
  isOpen,
  onClose,
  plantOptions,
  lastSyncedAt,
  onSync,
}: CreateLHCModalProps) {
  const toast = useToast();
  const navigate = useNavigate();

  const [lhcNumber, setLhcNumber] = useState('');
  const [lhcDate, setLhcDate] = useState('');
  const [selectedPlant, setSelectedPlant] = useState<SelectOption | null>(null);

  const createLHCMutation = useCreateLHCMutation({
    onSuccess: (data) => {
      showToast(toast, 'success', 'LHC berhasil dibuat');
      resetAndClose();
      navigate(`/collection-management/${data.id}`);
    },
    onError: (error) => {
      showToast(toast, 'error', error.message || 'Gagal membuat LHC');
    },
  });

  const resetAndClose = () => {
    setLhcNumber('');
    setLhcDate('');
    setSelectedPlant(null);
    onClose();
  };

  const handleSubmit = () => {
    if (!selectedPlant) return;

    createLHCMutation.mutate({
      lhc_no: lhcNumber.trim(),
      lhc_date: lhcDate,
      collection_plant_id: selectedPlant.value,
    });
  };

  const isFormValid =
    lhcNumber.trim() !== '' && lhcDate !== '' && selectedPlant !== null;

  return (
    <Modal
      title="Buat Laporan Harian Collection"
      name="create-lhc-modal"
      isOpen={isOpen}
      onClose={resetAndClose}
      size="2xl"
      isCentered
      hideAction
      scrollBehavior="outside"
      modalBodyProps={{ pb: 6 }}
      modalHeaderProps={{ fontSize: { base: '16px', md: '18px' }, pb: 2 }}
    >
      <Flex
        align="center"
        justify="space-between"
        mb={6}
        pb={4}
        borderBottom="1px solid"
        borderColor="gray.200"
        width="100%"
      >
        <SyncInfo lastSyncedAt={lastSyncedAt} onSync={onSync} />
      </Flex>

      <Flex gap={4} mb={6}>
        {/* Nomor LHC */}
        <FormControl flex={1}>
          <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">
            Nomor LHC
          </FormLabel>
          <Input
            placeholder="Tulis nomor LHC SOL"
            value={lhcNumber}
            onChange={(e) => setLhcNumber(e.target.value)}
            size="md"
          />
        </FormControl>

        {/* Tanggal LHC */}
        <FormControl flex={1}>
          <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">
            Tanggal LHC
          </FormLabel>
          <Input
            type="date"
            placeholder="Pilih tanggal"
            value={lhcDate}
            onChange={(e) => setLhcDate(e.target.value)}
            size="md"
          />
        </FormControl>

        {/* Plant / Cabang */}
        <FormControl flex={1}>
          <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">
            Plant / Cabang
          </FormLabel>
          <Box>
            <Select
              id="create-lhc-plant"
              placeholder="Pilih lokasi"
              options={plantOptions}
              value={selectedPlant}
              onChange={(newValue) =>
                setSelectedPlant(newValue as SelectOption | null)
              }
            />
          </Box>
        </FormControl>
      </Flex>

      {/* Submit Button */}
      <Flex justify="flex-end">
        <ChakraButton
          colorScheme="blue"
          bgColor="#0000A3"
          color="white"
          isDisabled={!isFormValid || createLHCMutation.isPending}
          isLoading={createLHCMutation.isPending}
          onClick={handleSubmit}
          px={6}
        >
          Proses LHC Baru
        </ChakraButton>
      </Flex>
    </Modal>
  );
}
